<section class="permissions-page">

  <div class="permissions-header">
    <h1>Manage Permissions</h1>
  </div>

  <!-- Role Header -->
  <div
    class="grid items-center mb-4 px-4 py-3 bg-white rounded-lg border"
    [style.gridTemplateColumns]="'1fr repeat(' + permissionRoles.length + ', 140px) 50px'"
  >
    <div class="font-semibold text-gray-700">Menu / Permission</div>
    @for (role of permissionRoles; track role.value) {
      <div class="text-center font-semibold bg-sky-500 text-white rounded-3xl py-1 mx-2">
        {{ role.label }}
      </div>
    }
    <!-- spacer cell to align with arrow column -->
    <div></div>
  </div>

  <div class="permissions-panel">

    @for (menu of menus; track menu.id) {
      <div class="permission-section border rounded-lg mb-4 overflow-hidden">

        <!-- ── Main Menu Row ── -->
        <div
          class="grid items-center px-4 py-3 bg-slate-50 border-b cursor-pointer hover:bg-slate-100 transition-colors"
          [style.gridTemplateColumns]="'1fr repeat(' + permissionRoles.length + ', 140px) 50px'"
          (click)="viewMenu(menu.id)"
        >
          <!-- Name cell -->
          <div class="font-semibold text-slate-800">{{ menu.menu_name }}</div>

          <!-- Role toggles -->
          @for (role of permissionRoles; track role.value) {
            <div class="flex justify-center" (click)="$event.stopPropagation()">
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  class="sr-only peer"
                  [checked]="selectedMainMenus[role.value + '::' + menu.id]"
                  [disabled]="role.value === '4'"
                  (change)="role.value !== '4' && toggleMainMenuForRole(menu.id, role.value, $event)"
                />
                <div class="w-10 h-5 bg-gray-200 rounded-full peer-checked:bg-sky-500 transition-colors
                       after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                       after:bg-white after:border after:border-gray-300 after:rounded-full
                       after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5">
                </div>
              </label>
            </div>
          }
          <div class="flex justify-center">
            <i
              class="ri-arrow-down-s-line text-xl text-slate-500 transition-transform duration-200"
              [class.rotate-180]="isMenuExpanded(menu.id)"
            ></i>
          </div>
        </div>
        @if (isMenuExpanded(menu.id)) {
          <div class="permission-rows">

            <!-- CASE 1: Sub-menus (Schedule, Settings, Personnel, Communication) -->
            @if (menu.subMenuChildren?.length) {

              @for (subMenu of menu.subMenuChildren; track subMenu.id) {

                <!-- Sub-menu Header Row -->
                <div
                  class="grid items-center px-4 py-3 bg-slate-100 border-b cursor-pointer hover:bg-slate-200 transition-colors"
                  [style.gridTemplateColumns]="'1fr repeat(' + permissionRoles.length + ', 140px) 50px'"
                  (click)="viewSubMenu(subMenu.id)"
                >
                  <!-- Sub-menu name (indented) -->
                  <div class="flex items-center gap-2 pl-6">
                    <i class="ri-corner-down-right-line text-slate-400 text-sm"></i>
                    <span class="font-semibold text-slate-700">{{ subMenu.menu_name }}</span>
                  </div>

                  <!-- Role toggles -->
                  @for (role of permissionRoles; track role.value) {
                    <div class="flex justify-center" (click)="$event.stopPropagation()">
                      <label class="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          class="sr-only peer"
                          [checked]="selectedMainMenus[role.value + '::' + subMenu.id]"
                          [disabled]="role.value === '4'"
                          (change)="role.value !== '4' && toggleSubMenuForRole(subMenu.id, role.value, $event)"
                        />
                        <div class="w-10 h-5 bg-gray-200 rounded-full peer-checked:bg-sky-500 transition-colors
                               after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                               after:bg-white after:border after:border-gray-300 after:rounded-full
                               after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5">
                        </div>
                      </label>
                    </div>
                  }

                  <!-- Arrow — last column -->
                  <div class="flex justify-center">
                    <i
                      class="ri-arrow-down-s-line text-lg text-slate-500 transition-transform duration-200"
                      [class.rotate-180]="isSubMenuExpanded(subMenu.id)"
                    ></i>
                  </div>
                </div>

                <!-- Sub-menu UAC Action Rows -->
                @if (isSubMenuExpanded(subMenu.id)) {
                  @for (row of getVisiblePermissionRows(subMenu); track row.id) {
                    <div
                      class="grid items-center px-4 py-3 border-b bg-white"
                      [style.gridTemplateColumns]="'1fr repeat(' + permissionRoles.length + ', 140px) 50px'"
                    >
                      <div
                        class="flex items-center gap-2"
                        [style.paddingLeft.px]="(row.level * 20) + 40"
                      >
                        @if (row.hasChildren) {
                          <i class="ri-arrow-right-s-fill text-sm text-slate-400"></i>
                        }
                        <span
                          [class.font-semibold]="row.hasChildren"
                          [class.text-slate-800]="row.hasChildren"
                          [class.text-slate-600]="!row.hasChildren"
                        >{{ row.label }}</span>
                      </div>

                      @for (role of permissionRoles; track role.value) {
                        <div class="flex justify-center">
                          <label class="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              class="sr-only peer"
                              [checked]="isPermissionEnabled(row, role.value)"
                              [disabled]="role.value === '4' || !canTogglePermission(row, role.value)"
                              (change)="role.value !== '4' && toggleRolePermission(row, role.value, $event)"
                            />
                            <div class="w-10 h-5 bg-gray-200 rounded-full peer-checked:bg-sky-500 transition-colors
                                   after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                                   after:bg-white after:rounded-full after:h-4 after:w-4
                                   after:transition-all peer-checked:after:translate-x-5">
                            </div>
                          </label>
                        </div>
                      }

                      <!-- empty spacer for arrow column -->
                      <div></div>
                    </div>
                  }
                }

              }

            } @else {

              @for (row of getVisiblePermissionRows(menu); track row.id) {
                <div
                  class="grid items-center px-4 py-3 border-b bg-white hover:bg-slate-50 transition-colors"
                  [style.gridTemplateColumns]="'1fr repeat(' + permissionRoles.length + ', 140px) 50px'"
                >
                  <div
                    class="flex items-center gap-2"
                    [style.paddingLeft.px]="row.level * 25"
                  >
                    @if (row.hasChildren) {
                      <i class="ri-arrow-right-s-fill text-sm text-slate-400"></i>
                    }
                    <span
                      [class.font-semibold]="row.hasChildren"
                      [class.text-slate-800]="row.hasChildren"
                      [class.text-slate-700]="!row.hasChildren"
                    >{{ row.label }}</span>
                  </div>

                  @for (role of permissionRoles; track role.value) {
                    <div class="flex justify-center">
                      <label class="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          class="sr-only peer"
                          [checked]="isPermissionEnabled(row, role.value)"
                          [disabled]="role.value === '4' || !canTogglePermission(row, role.value)"
                          (change)="role.value !== '4' && toggleRolePermission(row, role.value, $event)"
                        />
                        <div class="w-10 h-5 bg-gray-200 rounded-full peer-checked:bg-sky-500 transition-colors
                               after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                               after:bg-white after:rounded-full after:h-4 after:w-4
                               after:transition-all peer-checked:after:translate-x-5">
                        </div>
                      </label>
                    </div>
                  }

                  <!-- empty spacer for arrow column -->
                  <div></div>
                </div>
              }

              @if (!getVisiblePermissionRows(menu).length) {
                <div class="p-4 text-center text-gray-500">No permissions available</div>
              }

            }

          </div>
        }

      </div>
    }

    @if (!menus.length) {
      <div class="text-center py-8 text-gray-500">Loading permissions...</div>
    }

  </div>

  <div class="flex justify-end mt-5">
    <button
      type="button"
      class="btn btn-primary"
      [disabled]="isSavingPermissions"
      (click)="savePermissions()"
    >
      {{ isSavingPermissions ? 'Saving...' : 'Save Permissions' }}
    </button>
  </div>

</section>
